import { useCallback } from "react";

import { ArrowLeftIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonSize, ButtonVariant } from "@gx/dls/buttons/Button";
import FlexWrapper, { AlignItems } from "@gx/dls/containers/FlexWrapper";

import BaseHeader from "@/layouts/components/BaseHeader";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import { CREATE_PROJECT_FORM_STEPS } from "@/pages/projects/forms/create/constants";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";
import { CreateProjectFormStep } from "@/pages/projects/forms/create/types";

interface CreateProjectModalHeaderProps {
  onClose: () => void;
}

const CreateProjectModalHeader = ({ onClose }: CreateProjectModalHeaderProps) => {
  const { state, dispatch } = useCreateProjectFormContext();

  const handleGoBack = useCallback(() => {
    const previousStep = CREATE_PROJECT_FORM_STEPS[state.navigation.currentStepIndex - 1];
    if (!previousStep) {
      return;
    }

    dispatch({
      type: CreateProjectFormActionType.SET_STEP,
      payload: previousStep,
    });
  }, [dispatch, state.navigation.currentStepIndex]);

  return match(state.step)
    .returnType<React.ReactNode | null>()
    .with(CreateProjectFormStep.SELECT_PROJECT_SOURCES, () => (
      <BaseHeader
        title="Create project"
        description="Select the data sources for your project"
        onClose={onClose}
      />
    ))
    .with(CreateProjectFormStep.SELECT_MAPPING_METHOD, () => (
      <FlexWrapper alignItems={AlignItems.CENTER} gap={12} fillWidth>
        <Button
          icon={ArrowLeftIcon}
          variant={ButtonVariant.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={handleGoBack}
        />
        <BaseHeader
          title="Build project"
          description="Choose how you want to build your project"
          onClose={onClose}
        />
      </FlexWrapper>
    ))
    .with(CreateProjectFormStep.CONFIGURE_PROJECT, () => (
      <FlexWrapper alignItems={AlignItems.CENTER} gap={12} fillWidth>
        <Button
          icon={ArrowLeftIcon}
          variant={ButtonVariant.TERTIARY}
          size={ButtonSize.SMALL}
          onClick={handleGoBack}
        />
        <BaseHeader
          title="Configure project"
          description="Give your project a name and description"
          onClose={onClose}
        />
      </FlexWrapper>
    ))
    .exhaustive();
};

export default CreateProjectModalHeader;
